import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { useAuthStore } from '../../auth/store';
import { FormTitle } from '../../components/FormTitle';
import { AppToolbar } from '../../components/AppToolbar';
import { AppDialog } from '../../components/AppDialog';
import { AppDataGrid, type AppColumn } from '../../components/AppDataGrid';
import { BusyIcon, LoaderSymbol } from '../../components/loading';
import { useNotify } from '../../components/notifications';
import { useSaveFlow, useConfirm } from '../../components/confirm';
import { fmtLocalDate } from '../../utils/datetime';
import { EmployeeDetailForm, type EmployeeTab } from './EmployeeDetailForm';
import './usersDetail.css';

// ─────────────────────────────────────────────────────────────────────────
// User & Security → My Details — the signed-in user's OWN profile, rendered
// in the SAME six-tab ALPACA Employee detail layout as Users (shared
// EmployeeDetailForm).  Opens in read-only VIEW mode first (ALPACA parity,
// BO UI Standard v2 2026-07-02); Edit unlocks ONLY the four self-service
// fields (Email / Mobile / Office Phone / Language) with Save/Cancel at TOP
// and BOTTOM — everything else stays read-only in both modes.
//
//   Self-editable fields = exactly what PUT /api/v1/my-details accepts
//   (MyProfileUpdate): Email · Mobile Phone · Office Phone · Language.
//   Every other field renders READ-ONLY (greyed / cellcheck) — identity,
//   organization and role fields are managed from User & Security → Users.
//   Change My Password reuses the pre-existing handler.
//   New / Edit / Delete (2026-07-20, ALPACA EmployeeController.cs:213-240 —
//   Employee_DetailView is shared by My Details AND admin Employee editing,
//   and New/Edit/Delete there are gated by the SAME "MyDetails" nav
//   Create/Write/Delete flags as Edit/CopyRole): New deep-links to the
//   Users admin page's existing blank-create form (?new=1 — MyDetails has no
//   full multi-field create form of its own; reusing UsersAdminPage avoids a
//   duplicate). Delete removes the SIGNED-IN user's own account via the
//   EXISTING admin-gated DELETE /api/v1/security/users/{oid} endpoint, then
//   logs out and returns to /login (the account no longer exists to view).
//   Both confirmed first (standing rule 7); Delete uses the danger tone.
//   ALPACA parity (2026-07-20): the Roles tab (SHOW_ROLES_TAB) is hidden —
//   Employee_DetailView's Roles tab is Removed="True" in legacy on both
//   platforms. Copy Roles is a PUSH, matching legacy's CopyRoleFromEmployee
//   (EmployeeController.cs:1511-1597): the CURRENTLY OPEN record (yourself)
//   is the source; you pick one or more OTHER employees (excluding
//   Service/Administrator/yourself) and their roles are REPLACED with yours.
//   Reuses the EXISTING, already admin-gated + audited backend endpoint
//   (POST /api/v1/security/users/{oid}/copy-roles — UserAdminController.cs,
//   IsServiceOrAdmin()) that Users admin already calls, invoked once per
//   selected target with fromOid = my own oid. Two DELIBERATE departures
//   from legacy (non-negotiable project standards, not oversights): (1) a
//   confirmation dialog before the push (legacy has none — CLAUDE.md standing
//   rule 7 requires confirmation on every state-changing action), (2) a
//   success acknowledgement after (legacy shows nothing — standing rule 7
//   also requires this). Legacy's version has a real, verified authorization
//   gap (the action rides the same client-only "MyDetails Write" flag as
//   ordinary self-edit, with no server-side re-check, and could let an
//   ordinary user push roles onto other employees) — AI does NOT replicate
//   that: the backend endpoint independently re-checks IsServiceOrAdmin()
//   server-side regardless of what the UI shows, so a non-admin clicking this
//   gets a clear 403 error rather than a silent privilege escalation.
//   Picker (2026-07-20): legacy's Employee_ListView_CopyRole is a real
//   multi-column grid (DisplayName · FullName · UserName · Department · Role,
//   Model.xafml:22339-22348, checkbox multi-select), not a simple label list —
//   built here as a checkbox <AppDataGrid> inside <AppDialog>, using the SAME
//   fields GET /api/v1/security/users already returns (no backend change).
//
//   Photo / Signature (2026-07-02): MyProfileDto now carries the Employee
//   image bytes — shown READ-ONLY on Basic Information (managed from
//   User & Security → Users).  Still OMITTED (no state in MyProfileDto —
//   follow-up, do NOT invent endpoints): Contact "Country / State / City".
// ─────────────────────────────────────────────────────────────────────────

/** Mirrors the API MyProfileDto — ALPACA Employee_DetailView fields. */
interface MyProfile {
  oid: string;
  userName: string;
  displayName: string;
  firstName?: string | null; lastName?: string | null; initial?: string | null;
  jobTitle?: string | null; gender: number; birthday?: string | null;
  company?: string | null; department?: string | null; position?: string | null;
  employmentDate?: string | null; retireDate?: string | null;
  email?: string | null; mobilePhone?: string | null; officePhone?: string | null;
  homePhone?: string | null; fax?: string | null; otherPhone?: string | null;
  address?: string | null; zip?: string | null; resume?: string | null;
  role?: string | null; roles?: string | null; tFAEmail?: string | null;
  isLabUser: boolean; isLimsAdmin: boolean; isActive: boolean; isEnabledLanguage: boolean;
  language: number;
  /** dbo.Employee varbinary images — base64 on the wire (read-only here). */
  photo?: string | null; signature?: string | null;
}
const errMsg = (e: unknown) =>
  (e as { response?: { data?: { error?: { message?: string } } } }).response?.data?.error?.message
  ?? (e as { message?: string }).message ?? 'Request failed.';

// ALPACA parity columns (Employee_ListView_CopyRole, Model.xafml:22339-22345):
// DisplayName · FullName · UserName · Department · Role.
type UserRef = {
  oid: string; userName: string; displayName: string;
  fullName?: string | null; department?: string | null; role?: string | null;
};

function lcKeys(v: unknown): unknown {
  if (Array.isArray(v)) return v.map(lcKeys);
  if (v && typeof v === 'object') {
    const out: Record<string, unknown> = {};
    for (const [k, val] of Object.entries(v as Record<string, unknown>)) out[k.toLowerCase()] = lcKeys(val);
    return out;
  }
  return v;
}

const GENDERS: Record<number, string> = { 0: '—', 1: 'Male', 2: 'Female' };

// Photo/Signature arrive as raw base64 (System.Text.Json encodes the byte[]);
// build a display data URL, sniffing JPEG/GIF/PNG from the leading base64 chars
// (same helper as UsersAdminPage).
const imgDataUrl = (b64: string | null | undefined): string | null => {
  if (!b64) return null;
  const mime = b64.startsWith('/9j/') ? 'image/jpeg' : b64.startsWith('R0lGOD') ? 'image/gif' : 'image/png';
  return `data:${mime};base64,${b64}`;
};

function fmtDate(v?: string | null): string {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime()) || d.getFullYear() < 1901) return '';
  return fmtLocalDate(v);
}

const TAB_IDS = ['basic', 'org', 'contact', 'resume', 'user', 'roles'] as const;
type TabId = typeof TAB_IDS[number];

// ALPACA parity (2026-07-20): Employee_DetailView's Roles tab is present in the
// legacy BO but hidden — Removed="True" on BOTH BTLIMS.Web/Model.xafml:22156
// AND BTLIMS.Module.Web/Model.DesignedDiffs.xafml:2274 (belt-and-suspenders
// hide, not an oversight). Employee_DetailView backs My Details too, so this
// hides it there as well. UI-only — roleRows/roleCols are untouched.
const SHOW_ROLES_TAB = false;

/** The self-editable draft — mirrors MyProfileUpdate (the ONLY writable fields). */
type Draft = { email: string; mobilePhone: string; officePhone: string; language: number };
const draftFrom = (p: MyProfile): Draft => ({
  email: p.email ?? '', mobilePhone: p.mobilePhone ?? '', officePhone: p.officePhone ?? '', language: p.language ?? 1,
});

/**
 * My Details — the signed-in user's own profile (ALPACA Employee_DetailView),
 * EDITABLE like ALPACA for the self-service fields.  Self-scoped: the row is
 * resolved from the JWT, never the request body.
 */
export function MyDetailsPage() {
  const notify = useNotify();
  const runSave = useSaveFlow();
  const confirm = useConfirm();
  const navigate = useNavigate();

  const [profile, setProfile] = useState<MyProfile | null>(null);
  const [draft, setDraft] = useState<Draft>({ email: '', mobilePhone: '', officePhone: '', language: 1 });
  const [loading, setLoading] = useState(true);
  const [loadMs, setLoadMs] = useState<number | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dtab, setDtab] = useState<TabId>('basic');
  const [saving, setSaving] = useState(false);
  // Read-only VIEW mode first (ALPACA parity); Edit unlocks the four
  // self-service fields.  Save / Cancel return to view mode.
  const [mode, setMode] = useState<'view' | 'edit'>('view');

  // Change-My-Password dialog (existing wiring).
  const [pwdOpen, setPwdOpen] = useState(false);
  const [oldPwd, setOldPwd] = useState(''); const [newPwd, setNewPwd] = useState(''); const [confPwd, setConfPwd] = useState('');
  const [pwdBusy, setPwdBusy] = useState(false);
  const [deleteBusy, setDeleteBusy] = useState(false);

  // Copy Roles — PUSH direction (ALPACA parity): pick other employees, my
  // roles replace theirs. Multi-select checkbox grid (ALPACA
  // Employee_ListView_CopyRole parity — DisplayName/FullName/UserName/
  // Department/Role columns, not a plain label list).
  const [copyOpen, setCopyOpen] = useState(false);
  const [copyEmployees, setCopyEmployees] = useState<UserRef[]>([]);
  const [copyTargets, setCopyTargets] = useState<Set<string | number>>(new Set());
  const [copySearch, setCopySearch] = useState('');
  const [copyBusy, setCopyBusy] = useState(false);

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    const started = performance.now();
    try {
      const res = await api.get('/api/v1/my-details');
      const p = (res.data as { data: MyProfile }).data;
      setProfile(p);
      setDraft(draftFrom(p));
    } catch {
      setError('Could not load your profile. Please try again.');
    } finally {
      setLoadMs(performance.now() - started); setLoading(false); setRefreshing(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // ── Save (the EXISTING self-update API: PUT /api/v1/my-details) ──────────
  const save = () => {
    if (!profile) return;
    setSaving(true);
    runSave({
      save: async () => {
        await api.put('/api/v1/my-details', {
          email: draft.email.trim() || null,
          mobilePhone: draft.mobilePhone.trim() || null,
          officePhone: draft.officePhone.trim() || null,
          language: draft.language,
        });
      },
      successMessage: 'Profile saved successfully.',
      onSaved: () => { setMode('view'); void load(true); },
      onError: e => notify.error(errMsg(e)),
    }).finally(() => setSaving(false));
  };

  /** Cancel — discard the draft edits and return to read-only view mode. */
  const cancel = () => { if (profile) setDraft(draftFrom(profile)); setMode('view'); };

  // ── Change My Password (existing wiring) ─────────────────────────────────
  const changePassword = async () => {
    if (!newPwd) { notify.error('Enter the new password.'); return; }
    if (newPwd !== confPwd) { notify.error('Passwords do not match.'); return; }
    setPwdBusy(true);
    try {
      await api.put('/api/v1/my-details/password', { oldPassword: oldPwd, newPassword: newPwd, confirmPassword: confPwd });
      notify.success('Password changed successfully.');
      setPwdOpen(false); setOldPwd(''); setNewPwd(''); setConfPwd('');
    } catch (e) { notify.error(errMsg(e)); }
    finally { setPwdBusy(false); }
  };

  // ── New (ALPACA Employee_DetailView parity) ───────────────────────────────
  // My Details has no full multi-field create form of its own (only the four
  // MyProfileUpdate self-service fields) — deep-link to Users admin's
  // existing blank-create form rather than duplicating it.
  const goNewEmployee = () => navigate('/m/sec-users?new=1');

  // ── Delete my own account (ALPACA Employee_DetailView parity) ────────────
  // Reuses the EXISTING admin-gated DELETE /api/v1/security/users/{oid} — the
  // backend re-checks IsServiceOrAdmin() regardless of this button's
  // visibility, so a non-admin gets a clear 403 rather than actually deleting
  // their own account. On success there is no account left to view, so log
  // out and return to /login.
  const deleteMyAccount = async () => {
    if (!profile) return;
    const ok = await confirm.confirm({
      title: 'Delete my account?',
      message: `Permanently delete your own account (${profile.displayName || profile.userName})? This cannot be undone and you will be signed out immediately.`,
      tone: 'danger',
    });
    if (!ok) return;
    setDeleteBusy(true);
    try {
      await api.delete(`/api/v1/security/users/${profile.oid}`);
      notify.success('Account deleted.');
      useAuthStore.getState().logout();
      navigate('/login');
    } catch (e) { notify.error(errMsg(e)); setDeleteBusy(false); }
  };

  // ── Copy Roles — PUSH direction (ALPACA CopyRoleFromEmployee parity) ─────
  // Opens the shared multi-select picker of OTHER employees (Service /
  // Administrator / myself excluded, matching legacy's filter). On OK, pushes
  // MY current roles onto each selected target via the EXISTING, already
  // admin-gated + audited endpoint — one call per target (that endpoint is a
  // single-target replace, so a multi-target push is N calls, not a new bulk
  // endpoint). Confirmed first + acknowledged after (standing rule 7);
  // legacy does neither.
  const openCopyRoles = async () => {
    setCopyTargets(new Set()); setCopySearch(''); setCopyOpen(true);
    if (copyEmployees.length === 0) {
      try {
        const r = await api.get('/api/v1/security/users');
        const arr = ((lcKeys(r.data) as { data?: Record<string, unknown>[] }).data) ?? [];
        const mine = (profile?.userName ?? '').toLowerCase();
        setCopyEmployees(arr
          .map(u => ({
            oid: String(u.oid), userName: String(u.username ?? ''),
            displayName: String(u.displayname ?? ''), fullName: (u.fullname as string) ?? null,
            department: (u.department as string) ?? null, role: (u.role as string) ?? null,
          }))
          .filter(u => {
            const un = u.userName.toLowerCase();
            return un !== mine && un !== 'service' && un !== 'administrator';
          }));
      } catch (e) { notify.error(errMsg(e)); }
    }
  };

  const pushRolesToTargets = async (targetOids: string[]) => {
    if (!profile) return;
    if (targetOids.length === 0) { notify.warning('Select at least one employee.'); return; }
    const names = copyEmployees.filter(u => targetOids.includes(u.oid)).map(u => u.fullName || u.displayName).join(', ');
    const ok = await confirm.confirm({
      title: 'Copy roles?',
      message: `Push your current roles onto ${targetOids.length} employee${targetOids.length === 1 ? '' : 's'} (${names})? This REPLACES their existing roles.`,
      tone: 'danger',
    });
    if (!ok) return;
    setCopyBusy(true);
    try {
      const results = await Promise.allSettled(
        targetOids.map(oid => api.post(`/api/v1/security/users/${oid}/copy-roles`, { fromOid: profile.oid })));
      const failed = results.filter(r => r.status === 'rejected').length;
      const succeeded = results.length - failed;
      if (succeeded > 0) notify.success(`Roles copied to ${succeeded} employee${succeeded === 1 ? '' : 's'} successfully.`);
      if (failed > 0) notify.error(`Failed for ${failed} employee${failed === 1 ? '' : 's'} — check permissions and try again.`);
      setCopyOpen(false); setCopyTargets(new Set());
    } finally { setCopyBusy(false); }
  };

  const p = profile;

  // ── Roles tab — READ-ONLY here (assigned-role names from the profile) ────
  type RoleRow = { name: string };
  const roleRows: RoleRow[] = useMemo(
    () => (p?.roles ?? '').split(',').map(s => s.trim()).filter(Boolean).map(name => ({ name })),
    [p?.roles]);
  const roleCols: AppColumn<RoleRow>[] = [{ field: 'name', header: 'Role', minWidth: 240 }];

  // ── Copy Roles picker grid — ALPACA Employee_ListView_CopyRole column parity
  //    (DisplayName · FullName · UserName · Department · Role).
  const copyCols: AppColumn<UserRef>[] = [
    { field: 'displayName', header: 'Display Name', minWidth: 140 },
    { field: 'fullName', header: 'Full Name', minWidth: 160, text: u => u.fullName ?? '' },
    { field: 'userName', header: 'User Name', minWidth: 140 },
    { field: 'department', header: 'Department', minWidth: 140, text: u => u.department ?? '' },
    { field: 'role', header: 'Role', minWidth: 160, text: u => u.role ?? '' },
  ];
  const filteredCopyEmployees = useMemo(() => {
    const t = copySearch.trim().toLowerCase();
    if (!t) return copyEmployees;
    return copyEmployees.filter(u =>
      u.displayName.toLowerCase().includes(t) || (u.fullName ?? '').toLowerCase().includes(t) ||
      u.userName.toLowerCase().includes(t) || (u.department ?? '').toLowerCase().includes(t) ||
      (u.role ?? '').toLowerCase().includes(t));
  }, [copyEmployees, copySearch]);

  // ── six ALPACA Employee_DetailView tabs (shared EmployeeDetailForm) ───────
  // Editable = ONLY the MyProfileUpdate fields; everything else read-only.
  const tabs: EmployeeTab[] = p ? [
    {
      id: 'basic', label: 'Basic Information', icon: 'ti-user',
      fields: [
        { key: 'firstName', label: 'First Name', value: p.firstName ?? '' },
        { key: 'lastName', label: 'Last Name', value: p.lastName ?? '' },
        { key: 'initial', label: 'Initial', value: p.initial ?? '' },
        { key: 'jobTitle', label: 'Job Title', value: p.jobTitle ?? '' },
        { key: 'gender', label: 'Gender', value: GENDERS[p.gender] ?? '' },
        { key: 'birthday', label: 'Birthday', value: fmtDate(p.birthday) },
      ],
      // Photo / Signature (2026-07-02) — READ-ONLY images from MyProfileDto;
      // they are managed from User & Security → Users.
      content: (
        <div className="ud-grid cols-2">
          <div className="ud-field">
            <label>Photo</label>
            <div className="ud-img-box">
              {p.photo ? <img src={imgDataUrl(p.photo) ?? undefined} alt="Employee" /> : <div className="ud-img-empty">No photo</div>}
            </div>
          </div>
          <div className="ud-field">
            <label>Signature</label>
            <div className="ud-img-box">
              {p.signature ? <img src={imgDataUrl(p.signature) ?? undefined} alt="Signature" /> : <div className="ud-img-empty">No signature</div>}
            </div>
          </div>
        </div>
      ),
    },
    {
      id: 'org', label: 'Organization', icon: 'ti-building',
      fields: [
        { key: 'company', label: 'Division', value: p.company ?? '' },
        { key: 'department', label: 'Department', value: p.department ?? '' },
        { key: 'position', label: 'Position', value: p.position ?? '' },
        { key: 'employmentDate', label: 'Employment Date', value: fmtDate(p.employmentDate) },
        { key: 'retireDate', label: 'Retire Date', value: fmtDate(p.retireDate) },
      ],
    },
    {
      id: 'contact', label: 'Contact', icon: 'ti-address-book',
      // Country / State / City — OMITTED: not in MyProfileDto.
      fields: [
        { key: 'officePhone', label: 'Office Phone', value: draft.officePhone, onChange: v => setDraft(d => ({ ...d, officePhone: String(v) })) },
        { key: 'mobilePhone', label: 'Mobile Phone', value: draft.mobilePhone, onChange: v => setDraft(d => ({ ...d, mobilePhone: String(v) })) },
        { key: 'homePhone', label: 'Home Phone', value: p.homePhone ?? '' },
        { key: 'otherPhone', label: 'Other Phone', value: p.otherPhone ?? '' },
        { key: 'fax', label: 'Fax', value: p.fax ?? '' },
        // ALPACA parity: PropertyEditor Id="Email" Caption="Delivery Email" (Model.xafml:22026).
        { key: 'email', label: 'Delivery Email', type: 'email', value: draft.email, onChange: v => setDraft(d => ({ ...d, email: String(v) })) },
        { key: 'zip', label: 'Zip', value: p.zip ?? '' },
        { key: 'address', label: 'Address', type: 'textarea', value: p.address ?? '' },
      ],
    },
    {
      // Read-only here — Resume is admin-managed (User & Security → Users),
      // where it's now a rich-text (HTML) editor matching legacy's Resume tab
      // (2026-07-20). Rendered as HTML, not plain text, so saved formatting
      // (and old plain-text values, which contain no tags) both display correctly.
      id: 'resume', label: 'Resume', icon: 'ti-file-text',
      content: <div className="ud-ro" style={{ minHeight: 90 }} dangerouslySetInnerHTML={{ __html: p.resume || '—' }} />,
    },
    {
      id: 'user', label: 'User Information', icon: 'ti-shield-lock',
      fields: [
        // ALPACA parity: PropertyEditor Id="UserName" Caption="Email" (Model.xafml:22033).
        { key: 'userName', label: 'Email', value: p.userName },
        { key: 'displayName', label: 'Display Name (code)', value: p.displayName },
        { key: 'role', label: 'Role', value: p.role ?? '' },
        { key: 'tFAEmail', label: '2FA Email', value: p.tFAEmail ?? '' },
        // Language IS self-editable (MyProfileUpdate).  0 = Chinese · 1 = English.
        { key: 'language', label: 'Language', type: 'radio', value: draft.language,
          options: [{ value: '0', label: 'Chinese' }, { value: '1', label: 'English' }],
          onChange: v => setDraft(d => ({ ...d, language: Number(v) })) },
        { key: 'isLabUser', label: 'Is Lab User', type: 'checkbox', value: p.isLabUser },
        { key: 'isLimsAdmin', label: 'Is LIMS Administrator', type: 'checkbox', value: p.isLimsAdmin },
        { key: 'isActive', label: 'Is Active', type: 'checkbox', value: p.isActive },
        { key: 'isEnabledLanguage', label: 'Is Enabled Language', type: 'checkbox', value: p.isEnabledLanguage },
      ],
    },
    ...(SHOW_ROLES_TAB ? [{
      id: 'roles' as const, label: `Roles (${roleRows.length})`, icon: 'ti-users-group',
      content: (
        <>
          <div className="ud-sec-toolbar">
            <span className="ud-hint">Read-only — roles are assigned by an administrator (User &amp; Security → Users).</span>
          </div>
          <AppDataGrid<RoleRow>
            rows={roleRows}
            columns={roleCols}
            getRowId={r => r.name}
            emptyText="No roles assigned to your account."
          />
        </>
      ),
    }] : []),
  ] : [];

  return (
    <div className="ud-page">
      <FormTitle pageId="mydetails" loadMs={loadMs} loading={loading} />
      <div className="ud-sub">Your own profile — self-service fields are editable; everything else is managed by an administrator.</div>

      {/* Toolbar — form-specific Change My Password / Copy Roles + Refresh (no New/Delete). */}
      <div className="ud-toolbar-row">
        <AppToolbar
          left={<>
            <button className="ca-btn" onClick={() => { setOldPwd(''); setNewPwd(''); setConfPwd(''); setPwdOpen(true); }} title="Change My Password">
              <i className="ti ti-key" /> Change My Password
            </button>
            <button className="ca-btn" onClick={openCopyRoles} title="Copy Roles">
              <i className="ti ti-copy" /> Copy Roles
            </button>
          </>}
          onRefresh={() => load(true)} refreshing={refreshing}
        />
      </div>

      {loading ? (
        <div className="ud-detail" style={{ display: 'flex', alignItems: 'center', gap: 10, color: 'var(--ai-muted)' }}>
          <LoaderSymbol size={18} /> Loading your profile…
        </div>
      ) : error && !p ? (
        <div className="ud-detail"><div className="ud-err"><i className="ti ti-alert-circle" /> {error}</div></div>
      ) : p ? (
        <div className="ud-detail">
          {/* Header — "My Details – {record}" + breadcrumb (single record — no prev/next). */}
          <div className="ud-dhead">
            <div>
              <h2>My Details – {p.displayName || p.userName}</h2>
              <div className="ud-bc">
                <span>Home</span>
                <i className="ti ti-chevron-right" />
                <span>User &amp; Security</span>
                <i className="ti ti-chevron-right" />
                <span>My Details</span>
                <i className="ti ti-chevron-right" />
                <span>{p.displayName || p.userName}</span>
              </div>
            </div>
          </div>

          {/* Action bar — TOP (identical set at the bottom).  VIEW mode shows
              Edit (ALPACA parity); Edit unlocks the four self-service fields
              and the bar becomes Save (solid) / Cancel. */}
          <MyActionBar viewOnly={mode === 'view'} onEdit={() => setMode('edit')}
            onSave={save} onCancel={cancel} saving={saving}
            onNew={goNewEmployee} onDelete={deleteMyAccount} deleteBusy={deleteBusy} />

          <EmployeeDetailForm tabs={tabs} active={dtab} readOnly={mode === 'view'}
            onTabChange={id => setDtab(id as TabId)} />

          {/* Action bar — BOTTOM */}
          <MyActionBar bottom viewOnly={mode === 'view'} onEdit={() => setMode('edit')}
            onSave={save} onCancel={cancel} saving={saving}
            onNew={goNewEmployee} onDelete={deleteMyAccount} deleteBusy={deleteBusy} />
        </div>
      ) : null}

      {/* ── Change My Password dialog (shared AppDialog) ── */}
      <AppDialog open={pwdOpen} title="Change My Password" icon="ti-key" width={400}
        onClose={() => { if (!pwdBusy) setPwdOpen(false); }}
        footer={<>
          <button className="ca-btn" onClick={() => setPwdOpen(false)} disabled={pwdBusy}><span className="tb-i">↩︎</span> Cancel</button>
          <button className="ca-btn ca-btn-solid" onClick={changePassword} disabled={pwdBusy}>
            {pwdBusy ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} OK
          </button>
        </>}>
        <div className="ud-dlg-body">
          <div className="ud-field"><label>Old Password</label>
            <input className="ud-input" type="password" value={oldPwd} onChange={e => setOldPwd(e.target.value)} autoFocus /></div>
          <div className="ud-field"><label>New Password</label>
            <input className="ud-input" type="password" value={newPwd} onChange={e => setNewPwd(e.target.value)} /></div>
          <div className="ud-field"><label>Confirm Password</label>
            <input className="ud-input" type="password" value={confPwd} onChange={e => setConfPwd(e.target.value)} /></div>
        </div>
      </AppDialog>

      {/* ── Copy Roles picker — ALPACA Employee_ListView_CopyRole parity: a
          checkbox multi-select GRID (DisplayName/FullName/UserName/Department/
          Role columns + a filter box), not a plain label list. ── */}
      <AppDialog open={copyOpen} title="Copy Roles — push onto other employees" icon="ti-copy" width={760}
        onClose={() => { if (!copyBusy) setCopyOpen(false); }}
        footer={<>
          <button className="ca-btn" onClick={() => setCopyOpen(false)} disabled={copyBusy}><span className="tb-i">↩︎</span> Cancel</button>
          <button className="ca-btn ca-btn-solid" onClick={() => void pushRolesToTargets([...copyTargets].map(String))}
            disabled={copyBusy || copyTargets.size === 0}>
            {copyBusy ? <BusyIcon busy icon="ti-copy" /> : <i className="ti ti-copy" />} Copy Roles ({copyTargets.size})
          </button>
        </>}>
        <div className="ud-dlg-body">
          <span className="ud-hint">Pick one or more employees — your current roles will replace theirs.</span>
          <div className="list-search-row">
            <div className="list-search">
              <span className="tb-i">🔍</span>
              <input placeholder="Filter employees…" value={copySearch} onChange={e => setCopySearch(e.target.value)} />
              {copySearch && <span className="list-search-clear" onClick={() => setCopySearch('')}>×</span>}
            </div>
          </div>
          <AppDataGrid<UserRef>
            rows={filteredCopyEmployees}
            columns={copyCols}
            getRowId={u => u.oid}
            selectable
            selectedIds={copyTargets}
            onSelectionChange={setCopyTargets}
            emptyText="No other employees found."
            defaultPageSize={10}
           
          />
        </div>
      </AppDialog>
    </div>
  );
}

/** Detail action bar — rendered at TOP and BOTTOM.  Edit mode: Save (solid 💾)
 *  / Cancel (↩︎).  Read-only VIEW mode (ALPACA parity, canonical order New ·
 *  Edit · Delete — EmployeeController.cs:238-240): New / Delete deep-link to
 *  the shared account-level actions (goNewEmployee / deleteMyAccount). */
function MyActionBar({ bottom, viewOnly, onEdit, onSave, onCancel, saving, onNew, onDelete, deleteBusy }: {
  bottom?: boolean; viewOnly?: boolean; onEdit: () => void;
  onSave: () => void; onCancel: () => void; saving: boolean;
  onNew: () => void; onDelete: () => void; deleteBusy: boolean;
}) {
  return (
    <div className={`ud-actionbar${bottom ? ' bottom' : ''}`}>
      {viewOnly ? (
        <>
          <button className="ca-btn" onClick={onNew}>
            <span className="tb-i tb-i-green">＋</span> New
          </button>
          <button className="ca-btn" onClick={onEdit}>
            <span className="tb-i tb-i-blue">✎</span> Edit
          </button>
          <button className="ca-btn" onClick={onDelete} disabled={deleteBusy}>
            {deleteBusy ? <BusyIcon busy icon="ti-trash" /> : <span className="tb-i tb-i-red">🗑</span>} Delete
          </button>
        </>
      ) : (
        <>
          <button className="ca-btn ca-btn-solid" onClick={onSave} disabled={saving}>
            {saving ? <BusyIcon busy icon="ti-device-floppy" /> : <span className="tb-i">💾</span>} Save
          </button>
          <button className="ca-btn" onClick={onCancel} disabled={saving}>
            <span className="tb-i">↩︎</span> Cancel
          </button>
        </>
      )}
    </div>
  );
}
